import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, Sparkles, Activity, Filter, AlertTriangle, Info, Star, Brain, ExternalLink, Bot } from "lucide-react";
import { toast } from "sonner";
import { getOpportunities, getOpportunitiesMeta, getOpportunitiesTopBySide, getExpertAnalyses, toggleExpertAutotrade } from "../lib/api";
import { useAuth } from "../lib/auth";

const SIDE_TABS = [
    { key: "all", label: "TUTTI" },
    { key: "long", label: "SOLO LONG" },
    { key: "short", label: "SOLO SHORT" },
];

const BIAS_COLOR = {
    "Strong Long": "var(--green)",
    "Moderate Long": "#86efac",
    "Neutral": "var(--amber)",
    "Moderate Short": "#fca5a5",
    "Strong Short": "var(--red)",
};

const fmtPrice = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }));
const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
};

const scoreColor = (n) => n >= 81 ? "#22c55e" : n >= 61 ? "#3b82f6" : n >= 41 ? "#eab308" : "#ef4444";

export default function Opportunities() {
    const { user } = useAuth();
    const isAdmin = (user?.role === "admin");
    const [side, setSide] = useState("all");
    const [filters, setFilters] = useState({ min_trader: 75, min_long: 0, min_short: 0, min_volume: 0, min_confidence: 0, min_rs: 0, regime: "" });
    const [data, setData] = useState({ items: [], meta: {} });
    const [topBySide, setTopBySide] = useState({ long: [], short: [] });
    const [expert, setExpert] = useState({ by_symbol: {}, meta: {}, enabled: true });
    const [expertVisible, setExpertVisible] = useState(() => {
        // Persist user preference across sessions. Default = on when the backend
        // toggle allows it; the user override wins over subsequent env flips.
        try {
            const stored = localStorage.getItem("expertLayerVisible");
            return stored === null ? true : stored === "true";
        } catch { return true; }
    });
    const [loading, setLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);

    const toggleExpertVisible = () => {
        setExpertVisible(v => {
            const nv = !v;
            try { localStorage.setItem("expertLayerVisible", String(nv)); } catch { /* ignore */ }
            return nv;
        });
    };

    const handleToggleAutotrade = async () => {
        // Admin-only: flips the master switch in db.expert_layer_config so the
        // change takes effect at the next expert cycle without a redeploy.
        try {
            const res = await toggleExpertAutotrade();
            setExpert(e => ({ ...e, autotrade_enabled: res.autotrade_enabled }));
            toast.success(
                res.autotrade_enabled
                    ? "🤖 Autotrade ATTIVATO — il bot creerà pending orders al prossimo ciclo"
                    : "⏸ Autotrade DISATTIVATO — nessun nuovo pending verrà creato"
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Toggle autotrade fallito");
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const params = { side, ...filters, limit: 200 };
        if (!params.regime) delete params.regime;
        const tbsParams = { count: 10, ...filters };
        if (!tbsParams.regime) delete tbsParams.regime;
        Promise.all([
            getOpportunities(params),
            getOpportunitiesMeta(),
            getOpportunitiesTopBySide(tbsParams),
            // Expert layer is best-effort: failure must never break the page.
            getExpertAnalyses().catch(() => ({ by_symbol: {}, meta: {}, enabled: false })),
        ])
            .then(([opps, meta, tbs, exp]) => {
                if (cancelled) return;
                setData({ items: opps.items || [], meta: meta || {} });
                setTopBySide({ long: tbs?.long || [], short: tbs?.short || [] });
                setExpert({
                    by_symbol:          exp?.by_symbol || {},
                    meta:               exp?.meta || {},
                    enabled:            exp?.enabled !== false,
                    autotrade_enabled:  Boolean(exp?.autotrade_enabled),
                });
            })
            .catch(() => {
                if (cancelled) return;
                setData({ items: [], meta: {} });
                setTopBySide({ long: [], short: [] });
                setExpert({ by_symbol: {}, meta: {}, enabled: false });
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [side, filters]);

    // Master flag: only surface the expert overlay when the feature is on
    // both server-side (env toggle) AND client-side (user preference).
    const showExpert = expert.enabled && expertVisible;

    const items = data.items;
    const top = useMemo(() => items.slice(0, 8), [items]);
    // Top-10 LONG / SHORT — always 10 items each (when DB allows). Items
    // tagged `below_threshold: true` fill the list when fewer than 10 setups
    // cross the 75 primary gate. Hard filters (R/R + VWAP + MTF) are always
    // respected.
    const longs = topBySide.long;
    const shorts = topBySide.short;

    return (
        <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-[1400px] mx-auto" data-testid="opportunities-page">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3 mb-5 border-b border-soft pb-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                        <Sparkles size={22} style={{ color: "var(--brand-blue)" }} />
                        OPPORTUNITÀ
                    </h1>
                    <div className="text-[11px] text-mute mt-1">
                        Scansione automatica di tutta la piattaforma — risultati pronti, nessun calcolo on-demand
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] text-mute tracking-widest">ULTIMA SCANSIONE</div>
                    <div className="text-xs text-dim tnum">{fmtDate(data.meta?.last_scan_at)}</div>
                    <div className="text-[10px] text-mute mt-0.5">
                        {data.meta?.total_analyzed || 0} asset analizzati · {data.meta?.duration_seconds || 0}s
                    </div>
                    {data.meta?.last_partial_refresh && (
                        <div className="text-[9px] text-mute mt-0.5 italic" title="Refresh parziale tra una scansione completa e l'altra">
                            Ultimo refresh {data.meta.last_partial_refresh.scan_type === "crypto_refresh" ? "crypto" : "US stocks"}: {fmtDate(data.meta.last_partial_refresh.refreshed_at)}
                        </div>
                    )}
                    {expert.enabled && (
                        <button
                            type="button"
                            data-testid="expert-layer-toggle"
                            onClick={toggleExpertVisible}
                            title="Attiva/disattiva l'AI Trader (analisi con news + macro in tempo reale)"
                            className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-wider px-2 py-1 border"
                            style={{
                                color: showExpert ? "var(--brand-blue)" : "var(--text-mute)",
                                borderColor: showExpert ? "var(--brand-blue)" : "var(--soft)",
                                background: showExpert ? "rgba(59,130,246,0.10)" : "transparent",
                            }}>
                            <Brain size={11} />
                            AI TRADER {showExpert ? "ON" : "OFF"}
                        </button>
                    )}
                    {isAdmin && expert.enabled && (
                        <button
                            type="button"
                            data-testid="expert-autotrade-toggle"
                            onClick={handleToggleAutotrade}
                            title={expert.autotrade_enabled
                                ? "Il bot sta creando pending orders automaticamente. Clicca per SOSPENDERE."
                                : "Il bot NON crea trade autonomi. Clicca per ATTIVARE (pending orders al prossimo ciclo)."}
                            className="mt-1 ml-2 inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-wider px-2 py-1 border"
                            style={{
                                color: expert.autotrade_enabled ? "#22c55e" : "#9ca3af",
                                borderColor: expert.autotrade_enabled ? "#22c55e" : "var(--soft)",
                                background: expert.autotrade_enabled ? "rgba(34,197,94,0.10)" : "transparent",
                            }}>
                            <Bot size={11} />
                            AUTOTRADE {expert.autotrade_enabled ? "ON" : "OFF"}
                        </button>
                    )}
                    {showExpert && expert.meta?.finished_at && (
                        <div className="text-[9px] text-mute mt-1" title="Ciclo Expert Trader Layer">
                            AI aggiornato: {fmtDate(expert.meta.finished_at)} · prossimo {fmtDate(expert.meta.next_run_at)}
                        </div>
                    )}
                </div>
            </div>

            {/* Side tabs + filters toggle */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="flex items-center gap-1 border border-soft" data-testid="side-tabs">
                    {SIDE_TABS.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setSide(t.key)}
                            data-testid={`side-tab-${t.key}`}
                            className={`px-3 py-1.5 text-[10px] tracking-widest font-semibold ${
                                side === t.key ? "" : "text-mute hover:text-foreground"
                            }`}
                            style={side === t.key
                                ? { background: t.key === "short" ? "rgba(239,68,68,0.12)" : t.key === "long" ? "rgba(34,197,94,0.12)" : "rgba(59,130,246,0.12)",
                                    color: t.key === "short" ? "var(--red)" : t.key === "long" ? "var(--green)" : "var(--brand-blue)" }
                                : undefined}>
                            {t.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={() => setShowFilters(v => !v)}
                    data-testid="filters-toggle"
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-soft text-[10px] tracking-widest font-semibold text-dim hover:text-foreground">
                    <Filter size={11} />
                    FILTRI
                </button>
                <div className="ml-auto text-[10px] text-mute">
                    {items.length} opportunità · soglie ≥ 75
                </div>
            </div>

            {/* Filters panel */}
            {showFilters && (
                <div className="border border-soft bg-elev p-3 mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="filters-panel">
                    <FilterSlider label="Trader Score min" value={filters.min_trader} onChange={(v) => setFilters(f => ({ ...f, min_trader: v }))} min={75} />
                    <FilterSlider label="Long Score min" value={filters.min_long} onChange={(v) => setFilters(f => ({ ...f, min_long: v }))} />
                    <FilterSlider label="Short Score min" value={filters.min_short} onChange={(v) => setFilters(f => ({ ...f, min_short: v }))} />
                    <FilterSlider label="Volume Score min" value={filters.min_volume} onChange={(v) => setFilters(f => ({ ...f, min_volume: v }))} />
                    <FilterSlider label="AI Confidence min" value={filters.min_confidence} onChange={(v) => setFilters(f => ({ ...f, min_confidence: v }))} />
                    <FilterSlider label="Relative Strength min" value={filters.min_rs} onChange={(v) => setFilters(f => ({ ...f, min_rs: v }))} />
                    <label className="flex flex-col gap-1 text-[10px] text-mute tracking-wider col-span-2 sm:col-span-3 lg:col-span-2">
                        <span>MARKET REGIME</span>
                        <select
                            data-testid="filter-regime"
                            value={filters.regime}
                            onChange={(e) => setFilters(f => ({ ...f, regime: e.target.value }))}
                            className="bg-elev2 border border-soft px-2 py-1 text-xs"
                        >
                            <option value="">Tutti i regimi</option>
                            <option value="Strong Bull Trend">Strong Bull Trend</option>
                            <option value="Bull Trend">Bull Trend</option>
                            <option value="Range Market">Range Market</option>
                            <option value="High Volatility">High Volatility</option>
                            <option value="Bear Trend">Bear Trend</option>
                            <option value="Strong Bear Trend">Strong Bear Trend</option>
                        </select>
                    </label>
                </div>
            )}

            {loading && <LoadingState />}

            {!loading && items.length === 0 && longs.length === 0 && shorts.length === 0 && (
                <div className="border border-soft p-8 text-center text-mute text-sm" data-testid="empty-state">
                    <AlertTriangle size={20} className="mx-auto mb-2 text-amber-500" />
                    <div className="font-semibold mb-1">Nessuna opportunità trovata</div>
                    <div className="text-[11px]">
                        {data.meta?.last_scan_at ? "Riduci i filtri o riprova dopo la prossima scansione notturna (03:00 UTC)." : "Scansione in corso — attendi qualche minuto e ricarica."}
                    </div>
                </div>
            )}

            {!loading && (items.length > 0 || longs.length > 0 || shorts.length > 0) && (
                <>
                    {/* Modifica 3: informative banner about active hard filters */}
                    <div
                        data-testid="hard-filters-banner"
                        style={{
                            fontSize: "0.75rem",
                            color: "#6b7280",
                            padding: "4px 0 8px 0",
                            borderBottom: "1px solid var(--soft)",
                            marginBottom: "8px",
                        }}>
                        Solo setup con R/R ≥ 1.5 · MTF allineata · Prezzo entro 2σ dal VWAP
                    </div>
                    {/* TOP — visible only when at least one above-threshold opportunity exists */}
                    {items.length > 0 && (
                        <Section title="Top Opportunità del Giorno" subtitle="Le migliori scelte assolute per Trader Score + AI Confidence">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {top.map(o => <OpportunityCard key={`top-${o.symbol}`} o={o} compact={false} expert={showExpert ? expert.by_symbol[o.symbol] : null} />)}
                            </div>
                        </Section>
                    )}

                    {/* LONG */}
                    {(side === "all" || side === "long") && longs.length > 0 && (
                        <Section title="Migliori 10 Opportunità Long" subtitle={`Top ${longs.length} setup rialzisti — stessi parametri di selezione · ⚠ = sotto soglia primaria 75`}>
                            <OpportunityTable items={longs} side="long" expertBySymbol={showExpert ? expert.by_symbol : {}} />
                        </Section>
                    )}

                    {/* SHORT */}
                    {(side === "all" || side === "short") && shorts.length > 0 && (
                        <Section title="Migliori 10 Opportunità Short" subtitle={`Top ${shorts.length} setup ribassisti — stessi parametri di selezione · ⚠ = sotto soglia primaria 75`}>
                            <OpportunityTable items={shorts} side="short" expertBySymbol={showExpert ? expert.by_symbol : {}} />
                        </Section>
                    )}
                </>
            )}

            <Disclaimer />
        </div>
    );
}

// ------------------- sub-components -------------------

const Section = ({ title, subtitle, children }) => (
    <section className="mb-8">
        <div className="mb-3">
            <h2 className="text-lg font-bold tracking-wide">{title}</h2>
            <div className="text-[11px] text-mute">{subtitle}</div>
        </div>
        {children}
    </section>
);

function FilterSlider({ label, value, onChange, min = 0 }) {
    return (
        <label className="flex flex-col gap-1 text-[10px] text-mute tracking-wider">
            <span>{label.toUpperCase()}</span>
            <div className="flex items-center gap-2">
                <input
                    type="range"
                    min={min}
                    max={100}
                    step={1}
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="flex-1 accent-blue-500"
                />
                <span className="tnum text-[11px] text-dim font-semibold w-7 text-right">{value}</span>
            </div>
        </label>
    );
}

function OpportunityCard({ o, expert }) {
    const isShort = o.preferred_setup === "SHORT";
    const isLong = o.preferred_setup === "LONG";
    const accent = isShort ? "var(--red)" : isLong ? "var(--green)" : "var(--amber)";
    const Icon = isLong ? TrendingUp : isShort ? TrendingDown : Minus;
    const dominant = Math.max(o.scenarios?.bullish_pct || 0, o.scenarios?.sideways_pct || 0, o.scenarios?.bearish_pct || 0);
    // Opportunity Quality Ranking layer (additive). Older docs (pre-Feb-2026
    // scan) may be missing these fields — we degrade gracefully.
    const oqs = o.opportunity_quality_score;
    const isPremium = o.is_premium_setup === true;
    // Smart R/R Target 2 — same value the asset detail SmartSetup shows.
    // Pulls from the new `smart_levels` block populated by the nightly scan.
    // Color thresholds match the SmartSetup badge: ≥2.0 green, 1.5-2.0 amber, <1.5 red.
    const smartSide = isShort ? "short" : isLong ? "long" : null;
    const rrT2 = smartSide ? (o.smart_levels?.[smartSide]?.rr_t2 ?? null) : null;
    const rrT2Color = rrT2 == null ? "var(--text-mute)"
        : rrT2 >= 2.0 ? "#22c55e"
        : rrT2 >= 1.5 ? "#f59e0b"
        : "#ef4444";

    // Liquidity Momentum (Modifica 9) — surfaces the new momentum_score
    // and the institutional structure label from analyze_trend_structure.
    const mom = o.liquidity_map?.momentum;
    const momentumScore = mom?.momentum_score;
    const momentumLabel = mom?.momentum_label;
    const momentumWarning = mom?.momentum_warning === true;
    const momentumColor = momentumScore == null ? "var(--text-mute)"
        : momentumScore >= 70 ? "#22c55e"
        : momentumScore >= 40 ? "#f59e0b"
        : "#ef4444";
    const ts = o.trend_structure;
    const structureLabel = ts?.structure_label;
    const structureColor = structureLabel === "HH/HL INTATTA" ? "#22c55e"
        : structureLabel === "LH/LL INTATTA" ? "#ef4444"
        : "#9ca3af";
    // EQH/EQL nearest levels — show only one direction matching preferred_setup
    const eqhEql = o.liquidity_map?.eqh_eql;
    const nearestEql = eqhEql?.nearest_eql;
    const nearestEqh = eqhEql?.nearest_eqh;
    const stopHuntBadge = o.liquidity_map?.stop_hunt?.badge;

    return (
        <Link
            to={`/asset/${encodeURIComponent(o.symbol)}`}
            data-testid={`opp-card-${o.symbol}`}
            className="block border bg-elev hover:border-amber transition-colors p-3 relative"
            style={isPremium ? { borderColor: "#facc15" } : undefined}>
            {isPremium && (
                <div
                    data-testid={`premium-badge-${o.symbol}`}
                    title="Premium Setup: MTF allineata, volumi confermativi, R/R≥2.0, AI Conf ≥70, regime coerente"
                    className="absolute -top-1.5 -right-1.5 flex items-center gap-0.5 text-[9px] font-bold tracking-wider px-1.5 py-0.5 border"
                    style={{ background: "#facc15", color: "#0a0a0a", borderColor: "#facc15" }}>
                    <Star size={9} fill="currentColor" />PREMIUM
                </div>
            )}
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-sm tracking-wide" style={{ color: "var(--brand-blue)" }}>{o.symbol}</span>
                        <span className="text-[9px] uppercase text-mute tnum">{o.category}</span>
                    </div>
                    <div className="text-[11px] text-dim truncate" title={o.name}>{o.name}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-1.5 py-0.5 border whitespace-nowrap"
                    style={{ color: accent, borderColor: accent, background: `${accent}10` }}>
                    <Icon size={10} />{o.preferred_setup}
                </span>
            </div>

            <div className="flex items-baseline justify-between mb-2 gap-2">
                <span className="text-xl font-bold tnum">${fmtPrice(o.current_price)}</span>
                {rrT2 != null && (
                    <span
                        data-testid={`opp-rr-t2-${o.symbol}`}
                        title="Smart R/R Target 2 — stesso valore mostrato nel SmartSetup del dettaglio asset"
                        className="text-[10px] tnum font-bold tracking-wider px-1.5 py-0.5 border whitespace-nowrap"
                        style={{ color: rrT2Color, borderColor: rrT2Color, background: `${rrT2Color}15` }}>
                        R/R T2 {rrT2.toFixed(2)}
                    </span>
                )}
            </div>

            <div className="grid grid-cols-4 gap-1.5 mb-2">
                <ScorePill label="Trader" value={o.trader_score} />
                <ScorePill label="Conf." value={o.ai_confidence_score} />
                <ScorePill label="Vol" value={o.volume_score} />
                <ScorePill label="Qualità" value={oqs} hint="Opportunity Quality Score (MTF + Volumi + R/R + Regime + Momentum)" />
            </div>

            {/* Institutional Liquidity & Trend Structure badges */}
            {(momentumScore != null || structureLabel || stopHuntBadge) && (
                <div data-testid={`liquidity-row-${o.symbol}`} className="flex flex-wrap gap-1 mb-2">
                    {momentumScore != null && (
                        <span
                            data-testid={`opp-momentum-${o.symbol}`}
                            title={`ROC5 ${mom.roc5?.toFixed?.(2)}% · Thrust ${mom.thrust_quality_pct?.toFixed?.(0)}%`}
                            className="text-[9px] tnum font-bold tracking-wider px-1.5 py-0.5 border whitespace-nowrap"
                            style={{ color: momentumColor, borderColor: momentumColor, background: `${momentumColor}15` }}>
                            MOM {momentumScore}{momentumWarning ? " ⚠" : ""}
                        </span>
                    )}
                    {structureLabel && (
                        <span
                            data-testid={`opp-structure-${o.symbol}`}
                            title={ts?.correction_label || ""}
                            className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 border whitespace-nowrap"
                            style={{ color: structureColor, borderColor: structureColor }}>
                            {structureLabel}
                        </span>
                    )}
                    {stopHuntBadge && (
                        <span
                            data-testid={`opp-stophunt-${o.symbol}`}
                            title="Stop Hunt rilevato — sweep di liquidità nelle ultime 3 candele"
                            className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 border whitespace-nowrap"
                            style={{ color: "#a855f7", borderColor: "#a855f7", background: "rgba(168,85,247,0.15)" }}>
                            ⚡ {stopHuntBadge}
                        </span>
                    )}
                </div>
            )}

            {/* EQH/EQL nearest levels — show only the one relevant for the side */}
            {(isLong && nearestEql != null) || (isShort && nearestEqh != null) ? (
                <div className="text-[9px] text-mute mb-2 tnum">
                    {isLong ? (
                        <>EQL strutt. <span className="text-dim font-bold">${fmtPrice(nearestEql)}</span></>
                    ) : (
                        <>EQH strutt. <span className="text-dim font-bold">${fmtPrice(nearestEqh)}</span></>
                    )}
                </div>
            ) : null}

            <div className="text-[10px] text-mute tracking-wider mb-1">BIAS</div>
            <div className="text-[11px] font-bold mb-2" style={{ color: BIAS_COLOR[o.final_bias] || "var(--text-dim)" }}>
                {o.final_bias}
            </div>

            {dominant > 0 && (
                <div className="text-[9px] text-mute flex items-center gap-2">
                    <span>📈 {o.scenarios?.bullish_pct}%</span>
                    <span>➖ {o.scenarios?.sideways_pct}%</span>
                    <span>📉 {o.scenarios?.bearish_pct}%</span>
                </div>
            )}

            {o.badges?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {o.badges.slice(0, 3).map((b, i) => (
                        <span key={i} className="text-[8px] tracking-wider uppercase px-1.5 py-0.5 border border-soft text-dim">
                            {b}
                        </span>
                    ))}
                </div>
            )}

            {expert && <ExpertBlock expert={expert} />}
        </Link>
    );
}

const ScorePill = ({ label, value, hint }) => {
    const c = scoreColor(value || 0);
    return (
        <div className="flex flex-col items-center border border-soft py-1" title={hint || `${label} Score`}>
            <span className="text-[8px] text-mute tracking-wider">{label.toUpperCase()}</span>
            <span className="tnum font-bold text-xs" style={{ color: c }}>{value ?? 0}</span>
        </div>
    );
};

// ---- Expert Trader Layer helpers ------------------------------------------
const EXPERT_DIRECTION_META = {
    long:     { label: "LONG",         color: "#22c55e", Icon: TrendingUp },
    short:    { label: "SHORT",        color: "#ef4444", Icon: TrendingDown },
    no_trade: { label: "NON TRADARE",  color: "#9ca3af", Icon: Minus },
};

const CLASSIFICATION_META = {
    STRONG_OPPORTUNITY: { label: "STRONG",       color: "#10b981" },
    OPPORTUNITY:        { label: "OPPORTUNITY",  color: "#22c55e" },
    WATCH:              { label: "WATCH",        color: "#f59e0b" },
    AVOID:              { label: "AVOID",        color: "#9ca3af" },
    INVALIDATED:        { label: "INVALIDATED",  color: "#ef4444" },
};

const ConvictionDots = ({ n }) => {
    const count = Math.max(0, Math.min(5, Number(n) || 0));
    return (
        <span className="inline-flex gap-0.5" title={`Conviction ${count}/5`}>
            {[1, 2, 3, 4, 5].map(i => (
                <span
                    key={i}
                    className="inline-block rounded-full"
                    style={{
                        width: 6, height: 6,
                        background: i <= count ? "currentColor" : "transparent",
                        border: "1px solid currentColor",
                    }}
                />
            ))}
        </span>
    );
};

function ExpertBlock({ expert }) {
    const meta = EXPERT_DIRECTION_META[expert.direction] || EXPERT_DIRECTION_META.no_trade;
    const clsMeta = CLASSIFICATION_META[expert.opportunity_classification] || CLASSIFICATION_META.WATCH;
    const Icon = meta.Icon;
    const notRecommended = expert.direction === "no_trade";
    return (
        <div
            data-testid={`expert-block-${expert.symbol}`}
            className="mt-3 pt-3 border-t border-soft"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            style={notRecommended ? { opacity: 0.85 } : undefined}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5 text-[10px] text-mute tracking-widest">
                    <Brain size={11} style={{ color: "var(--brand-blue)" }} />
                    AI TRADER
                </div>
                <div className="flex items-center gap-1">
                    <span
                        data-testid={`expert-classification-${expert.symbol}`}
                        className="inline-flex items-center text-[9px] font-bold tracking-wider px-1.5 py-0.5 border"
                        style={{ color: clsMeta.color, borderColor: clsMeta.color, background: `${clsMeta.color}15` }}>
                        {clsMeta.label}
                    </span>
                    <span
                        data-testid={`expert-direction-${expert.symbol}`}
                        className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-1.5 py-0.5 border"
                        style={{ color: meta.color, borderColor: meta.color, background: `${meta.color}15` }}>
                        <Icon size={10} />{meta.label}
                        <span style={{ color: meta.color }}><ConvictionDots n={expert.conviction} /></span>
                    </span>
                </div>
            </div>

            {(expert.agrees_with_oqs === false || expert.technical_only || expert.levels_valid === false) && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                    {expert.agrees_with_oqs === false && !notRecommended && (
                        <span
                            data-testid={`expert-disagree-${expert.symbol}`}
                            title="AI Trader vede una direzione diversa dallo scanner OQS"
                            className="text-[8px] font-bold tracking-wider px-1.5 py-0.5 border"
                            style={{ color: "#f59e0b", borderColor: "#f59e0b", background: "rgba(245,158,11,0.10)" }}>
                            ⚠ DISACCORDO OQS
                        </span>
                    )}
                    {expert.technical_only && (
                        <span title="Nessun contesto news/macro trovato — analisi solo tecnica"
                            className="text-[8px] font-bold tracking-wider px-1.5 py-0.5 border"
                            style={{ color: "#94a3b8", borderColor: "#94a3b8" }}>
                            SOLO TECNICA
                        </span>
                    )}
                    {expert.levels_valid === false && (
                        <span title={`Livelli non validi: ${expert.validation_reason || "invarianza SL/entry/target rotta"}`}
                            className="text-[8px] font-bold tracking-wider px-1.5 py-0.5 border"
                            style={{ color: "#ef4444", borderColor: "#ef4444", background: "rgba(239,68,68,0.10)" }}>
                            LEVELS INVALIDATI
                        </span>
                    )}
                </div>
            )}

            {expert.thesis && (
                <div data-testid={`expert-thesis-${expert.symbol}`} className="text-[11px] text-white leading-snug mb-2 italic">
                    &ldquo;{expert.thesis}&rdquo;
                </div>
            )}

            {expert.confirmations?.length > 0 && (
                <div className="mb-1.5">
                    <div className="text-[9px] text-mute uppercase tracking-wider mb-0.5">A favore</div>
                    <ul className="text-[10px] text-dim leading-snug pl-3 list-disc">
                        {expert.confirmations.slice(0, 4).map((r, i) => (
                            <li key={i} className="mb-0.5" style={{ color: "#86efac" }}>{r}</li>
                        ))}
                    </ul>
                </div>
            )}
            {expert.contradictions?.length > 0 && (
                <div className="mb-1.5">
                    <div className="text-[9px] text-mute uppercase tracking-wider mb-0.5">Contro</div>
                    <ul className="text-[10px] text-dim leading-snug pl-3 list-disc">
                        {expert.contradictions.slice(0, 3).map((r, i) => (
                            <li key={i} className="mb-0.5" style={{ color: "#fca5a5" }}>{r}</li>
                        ))}
                    </ul>
                </div>
            )}
            {expert.invalidation_reason && (
                <div className="text-[9px] text-mute mb-1">
                    <span className="uppercase tracking-wider">Invalidazione:</span>{" "}
                    <span className="text-dim">{expert.invalidation_reason}</span>
                </div>
            )}

            {(expert.entry_price || expert.stop_loss || expert.target_price) && (
                <div className="grid grid-cols-4 gap-1 mt-2 text-[9px]">
                    <div className="border border-soft px-1 py-0.5">
                        <div className="text-mute">ENTRY</div>
                        <div className="tnum text-dim">{expert.entry_price != null ? `$${fmtPrice(expert.entry_price)}` : "—"}</div>
                    </div>
                    <div className="border border-soft px-1 py-0.5">
                        <div className="text-mute">STOP</div>
                        <div className="tnum" style={{ color: "#fca5a5" }}>{expert.stop_loss != null ? `$${fmtPrice(expert.stop_loss)}` : "—"}</div>
                    </div>
                    <div className="border border-soft px-1 py-0.5">
                        <div className="text-mute">TARGET</div>
                        <div className="tnum" style={{ color: "#86efac" }}>{expert.target_price != null ? `$${fmtPrice(expert.target_price)}` : "—"}</div>
                    </div>
                    <div className="border border-soft px-1 py-0.5">
                        <div className="text-mute">R/R</div>
                        <div className="tnum text-dim">{expert.risk_reward_ratio != null ? `${expert.risk_reward_ratio}x` : "—"}</div>
                    </div>
                </div>
            )}
            {expert.citations?.length > 0 && (
                <div className="text-[9px] text-mute mt-1.5 flex items-center gap-1 flex-wrap">
                    <ExternalLink size={9} />
                    <span>{expert.citations.length} fonti web</span>
                </div>
            )}
        </div>
    );
}

function ExpertPill({ expert, symbol }) {
    if (!expert) return <span className="text-mute text-[10px]">—</span>;
    const meta = EXPERT_DIRECTION_META[expert.direction] || EXPERT_DIRECTION_META.no_trade;
    const clsMeta = CLASSIFICATION_META[expert.opportunity_classification] || null;
    const Icon = meta.Icon;
    const tooltipParts = [
        `AI Trader → ${meta.label} (conviction ${expert.conviction}/5)`,
        clsMeta ? `Classification: ${clsMeta.label}` : null,
        expert.thesis ? `Thesis: ${expert.thesis}` : null,
        expert.agrees_with_oqs === false && !expert.no_trade ? "⚠ Disaccordo con OQS" : null,
        expert.technical_only ? "Solo analisi tecnica" : null,
        expert.invalidation_reason ? `Invalidazione: ${expert.invalidation_reason}` : null,
    ].filter(Boolean).join("\n");
    return (
        <span className="inline-flex items-center gap-1">
            <span
                data-testid={`expert-pill-${symbol}`}
                title={tooltipParts}
                className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider px-1.5 py-0.5 border whitespace-nowrap"
                style={{ color: meta.color, borderColor: meta.color, background: `${meta.color}15` }}>
                <Icon size={9} />
                {meta.label}
                <ConvictionDots n={expert.conviction} />
                {expert.agrees_with_oqs === false && !expert.no_trade && (
                    <AlertTriangle size={9} style={{ color: "#f59e0b" }} />
                )}
            </span>
            {clsMeta && (
                <span
                    className="text-[8px] font-bold tracking-wider px-1 py-0.5 border"
                    style={{ color: clsMeta.color, borderColor: clsMeta.color }}>
                    {clsMeta.label}
                </span>
            )}
        </span>
    );
}

function OpportunityTable({ items, side, expertBySymbol }) {
    const showExpertColumn = expertBySymbol && Object.keys(expertBySymbol).length > 0;
    return (
        <div className="border border-soft bg-elev overflow-x-auto" data-testid={`opp-table-${side}`}>
            <table className="w-full text-[11px] tnum">
                <thead className="text-[9px] tracking-widest text-mute border-b border-soft">
                    <tr>
                        <th className="text-left px-3 py-2">SIMBOLO</th>
                        <th className="text-right px-2 py-2">PREZZO</th>
                        <th className="text-right px-2 py-2">TRADER</th>
                        <th className="text-right px-2 py-2">CONF.</th>
                        <th className="text-right px-2 py-2">VOL.</th>
                        <th className="text-right px-2 py-2" title="Opportunity Quality Score">QUAL.</th>
                        <th className="text-right px-2 py-2" title="Smart Risk/Reward del Target 2 — stesso valore dello SmartSetup">R/R T2</th>
                        <th className="text-right px-2 py-2">{side === "short" ? "SHORT" : "LONG"}</th>
                        {showExpertColumn && (
                            <th className="text-left px-2 py-2" title="Verdetto AI Trader (Perplexity Sonar + web search)">AI TRADER</th>
                        )}
                        <th className="text-left px-3 py-2">BIAS</th>
                        <th className="text-left px-3 py-2 hidden lg:table-cell">SCENARI</th>
                        <th className="text-left px-3 py-2 hidden md:table-cell">BADGE</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(o => {
                        const dirScore = side === "short" ? o.short_score : o.long_score;
                        const rrT2 = o.smart_levels?.[side === "short" ? "short" : "long"]?.rr_t2 ?? null;
                        const rrColor = rrT2 == null ? "var(--text-mute)"
                            : rrT2 >= 2.0 ? "#22c55e"
                            : rrT2 >= 1.5 ? "#f59e0b"
                            : "#ef4444";
                        return (
                            <tr key={o.symbol} className="border-b border-soft hover:bg-elev2"
                                style={o.is_premium_setup ? { background: "rgba(250,204,21,0.06)" } : undefined}>
                                <td className="px-3 py-2">
                                    <Link to={`/asset/${encodeURIComponent(o.symbol)}`} className="block" data-testid={`opp-row-${o.symbol}`}>
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="font-bold" style={{ color: "var(--brand-blue)" }}>{o.symbol}</span>
                                            {o.is_premium_setup && (
                                                <span data-testid={`premium-row-${o.symbol}`}
                                                    title="Premium Setup"
                                                    className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1 py-0.5"
                                                    style={{ background: "#facc15", color: "#0a0a0a" }}>
                                                    <Star size={8} fill="currentColor" />PREMIUM
                                                </span>
                                            )}
                                            {o.below_threshold && (
                                                <span data-testid={`below-threshold-${o.symbol}`}
                                                    title="Setup completato sotto la soglia primaria 75 (rispetta comunque R/R + VWAP + MTF)"
                                                    className="inline-flex items-center gap-0.5 text-[8px] font-bold px-1 py-0.5 border"
                                                    style={{ color: "#f59e0b", borderColor: "#f59e0b" }}>
                                                    ⚠ SOTTO SOGLIA 75
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-mute truncate max-w-[180px]" title={o.name}>{o.name}</div>
                                    </Link>
                                </td>
                                <td className="text-right px-2 py-2">${fmtPrice(o.current_price)}</td>
                                <td className="text-right px-2 py-2 font-bold" style={{ color: scoreColor(o.trader_score) }}>{o.trader_score}</td>
                                <td className="text-right px-2 py-2 font-bold" style={{ color: scoreColor(o.ai_confidence_score) }}>{o.ai_confidence_score}</td>
                                <td className="text-right px-2 py-2 font-bold" style={{ color: scoreColor(o.volume_score) }}>{o.volume_score}</td>
                                <td className="text-right px-2 py-2 font-bold" style={{ color: scoreColor(o.opportunity_quality_score) }}>{o.opportunity_quality_score ?? "—"}</td>
                                <td className="text-right px-2 py-2 font-bold tnum" data-testid={`opp-row-rr-t2-${o.symbol}`} style={{ color: rrColor }}>{rrT2 != null ? rrT2.toFixed(2) : "—"}</td>
                                <td className="text-right px-2 py-2 font-bold" style={{ color: scoreColor(dirScore) }}>{dirScore}</td>
                                {showExpertColumn && (
                                    <td className="px-2 py-2">
                                        <ExpertPill expert={expertBySymbol[o.symbol]} symbol={o.symbol} />
                                    </td>
                                )}
                                <td className="px-3 py-2 font-semibold" style={{ color: BIAS_COLOR[o.final_bias] }}>{o.final_bias}</td>
                                <td className="px-3 py-2 hidden lg:table-cell text-[10px] text-mute">
                                    {o.scenarios ? `${o.scenarios.bullish_pct}/${o.scenarios.sideways_pct}/${o.scenarios.bearish_pct}` : "—"}
                                </td>
                                <td className="px-3 py-2 hidden md:table-cell">
                                    <div className="flex flex-wrap gap-1">
                                        {(o.badges || []).slice(0, 2).map((b, i) => (
                                            <span key={i} className="text-[8px] tracking-wider uppercase px-1.5 py-0.5 border border-soft text-dim">{b}</span>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

const LoadingState = () => (
    <div className="space-y-3" data-testid="opportunities-loading">
        <div className="text-xs text-mute flex items-center gap-2">
            <Activity size={12} className="animate-pulse" />
            Caricamento opportunità precomputate…
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-44 bg-elev2 animate-pulse border border-soft" />)}
        </div>
    </div>
);

const Disclaimer = () => (
    <div className="mt-8 border-t border-soft pt-4 text-[10px] text-mute flex items-start gap-2 leading-relaxed">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <div>
            <strong>Disclaimer:</strong> i punteggi e i bias sono il risultato di un&apos;analisi algoritmica
            multi-indicatore (trend, S/R, volumi, VWAP, A/D, breakout) eseguita ogni notte sull&apos;intera
            piattaforma. NON garantiscono profitti e NON costituiscono consulenza finanziaria personalizzata.
            Verifica sempre il piano completo (entry/stop/target) nella scheda dell&apos;asset prima di operare.
        </div>
    </div>
);
